/**
 * Run-scoped provenance for FactSet formula output scales.
 *
 * Formula generation and workbook formatting occur in separate tool rounds.
 * This registry carries the semantic hint between those rounds and commits
 * cell provenance only after a successful workbook write.
 */

import type { WorkbookModification } from "./types";
import {
  extractFactSetDataItems,
  inferFactSetFormatHint,
  isAtomicFactSetDataItemFormula,
  isDirectFactSetFormula,
  type FactSetFormatHint,
  type FactSetFormatHintConfidence,
} from "./factsetValueSemantics";

export interface TrackedFactSetCell {
  workbookName: string;
  sheetName: string;
  address: string;
  formula: string;
  hint: FactSetFormatHint;
}

const CONFIDENCE_RANK: Record<FactSetFormatHintConfidence, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

export class FactSetFormulaFormatRegistry {
  private readonly formulaHints = new Map<string, FactSetFormatHint>();
  private readonly itemHints = new Map<string, FactSetFormatHint>();
  private readonly cellHints = new Map<string, TrackedFactSetCell>();
  private readonly attemptedCellHints = new Map<string, TrackedFactSetCell>();

  /** Capture one or batched `generate_formula` output. */
  recordGenerateFormulaOutput(output: unknown): void {
    const record = asRecord(output);
    if (!record) return;
    const entries = Array.isArray(record.results) ? record.results : [record];

    for (const rawEntry of entries) {
      const entry = asRecord(rawEntry);
      if (!entry || typeof entry.error === "string") continue;
      const formula =
        typeof entry.formula === "string"
          ? entry.formula
          : typeof entry.formula_template === "string"
            ? entry.formula_template
            : undefined;
      if (!formula) continue;
      const formulaTemplate =
        typeof entry.formula_template === "string"
          ? entry.formula_template
          : undefined;
      const provided = parseFormatHint(entry.formatHint);
      const inferred = inferFactSetFormatHint({
        formula,
        formulaTemplate,
      });
      const hint = betterHint(provided, inferred);
      this.recordFormulaHint(formula, hint);
      if (formulaTemplate) this.recordFormulaHint(formulaTemplate, hint);
    }
  }

  /** Resolve the best available hint for an actual workbook formula. */
  resolveFormula(formula: unknown): FactSetFormatHint | undefined {
    if (!isDirectFactSetFormula(formula)) return undefined;
    const exact = this.formulaHints.get(normalizeFormula(formula));
    if (exact) return exact;

    const items = extractFactSetDataItems(formula);
    if (
      items.length === 1 &&
      isAtomicFactSetDataItemFormula(formula, items[0])
    ) {
      const itemHint = this.itemHints.get(items[0]);
      if (itemHint) return itemHint;
    }
    return inferFactSetFormatHint({ formula });
  }

  /** Address-level state after a modification, without committing it. */
  previewCells(
    modifications: WorkbookModification,
    workbookName?: string,
  ): TrackedFactSetCell[] {
    const preview = new Map(this.cellHints);
    this.applyModifications(preview, modifications, workbookName);
    return filterTrackedCellsByWorkbook(preview.values(), workbookName);
  }

  /** Formula cells written by this modification only. */
  previewFormulaWrites(
    modifications: WorkbookModification,
    workbookName?: string,
  ): TrackedFactSetCell[] {
    const writes = new Map<string, TrackedFactSetCell>();
    this.applyModifications(writes, modifications, workbookName);
    return filterTrackedCellsByWorkbook(writes.values(), workbookName);
  }

  /** Commit provenance after the host confirms the write succeeded. */
  commitSuccessfulModifications(
    modifications: WorkbookModification,
    workbookName?: string,
  ): void {
    this.applyModifications(this.cellHints, modifications, workbookName);
    this.applyModifications(
      this.attemptedCellHints,
      modifications,
      workbookName,
    );
  }

  /** Retain attempted formula targets for live end-of-run verification only. */
  rememberAttemptedModifications(
    modifications: WorkbookModification,
    workbookName?: string,
  ): void {
    const finalWrites = containsStructuralProvenanceOperation(modifications)
      ? new Map([
          ...Array.from(this.cellHints.entries()),
          ...Array.from(this.attemptedCellHints.entries()),
        ])
      : new Map<string, TrackedFactSetCell>();
    this.applyModifications(finalWrites, modifications, workbookName);
    for (const [key, cell] of Array.from(finalWrites.entries())) {
      this.attemptedCellHints.set(key, cell);
    }
  }

  /** Successful plus attempted targets; callers verify the live formula. */
  listSafetyCells(workbookName?: string): TrackedFactSetCell[] {
    const combined = new Map(this.cellHints);
    for (const [key, cell] of Array.from(this.attemptedCellHints.entries())) {
      combined.set(key, cell);
    }
    const cells = Array.from(combined.values());
    if (workbookName === undefined) return cells;
    const workbookKey = normalizeWorkbookName(workbookName);
    return cells.filter(
      (cell) => normalizeWorkbookName(cell.workbookName) === workbookKey,
    );
  }

  private recordFormulaHint(
    formula: string,
    hint: FactSetFormatHint,
  ): void {
    if (!isDirectFactSetFormula(formula)) return;
    const formulaKey = normalizeFormula(formula);
    this.formulaHints.set(
      formulaKey,
      betterHint(this.formulaHints.get(formulaKey), hint),
    );

    const items = extractFactSetDataItems(formula);
    if (
      items.length !== 1 ||
      !isAtomicFactSetDataItemFormula(formula, items[0])
    ) {
      return;
    }
    const dataItem = items[0];
    const existing = this.itemHints.get(dataItem);
    this.itemHints.set(dataItem, mergeItemHints(existing, hint, dataItem));
  }

  private applyModifications(
    target: Map<string, TrackedFactSetCell>,
    modifications: WorkbookModification,
    workbookName?: string,
  ): void {
    const workbook = normalizeWorkbookName(workbookName);
    for (const sheet of modifications.sheets ?? []) {
      if (sheet.action === "delete" && sheet.name) {
        clearTrackedSheet(target, workbook, sheet.name);
        continue;
      }
      if (
        sheet.action === "rename" &&
        sheet.name &&
        sheet.newName
      ) {
        renameTrackedSheet(
          target,
          workbook,
          sheet.name,
          sanitizeExcelWorksheetName(sheet.newName),
        );
        continue;
      }
      if (sheet.action === "move" && sheet.name && sheet.move) {
        const sourceWorkbook = normalizeWorkbookName(
          sheet.move.sourceWorkbookName ?? workbookName,
        );
        const targetWorkbook = normalizeWorkbookName(
          sheet.move.targetWorkbookName ?? workbookName,
        );
        if (sourceWorkbook !== targetWorkbook) {
          // A cross-workbook move preserves the cell's current format, but
          // this run remains pinned to the source workbook. Invalidate both
          // sides rather than retaining provenance that cannot be verified
          // or safely corrected through the pinned apply path.
          clearTrackedSheet(target, sourceWorkbook, sheet.name);
          clearTrackedSheet(target, targetWorkbook, sheet.name);
        }
      }
    }

    for (const worksheet of modifications.worksheets ?? []) {
      const sheetName =
        typeof worksheet.sheetName === "string"
          ? worksheet.sheetName.trim()
          : "";
      if (!sheetName) continue;

      for (const cell of worksheet.cells ?? []) {
        if (!cell.address) continue;
        if (cell.action === "clear") {
          clearTrackedRange(target, workbook, sheetName, cell.address);
          continue;
        }
        if (cell.action !== "set") continue;
        if (cell.formula === undefined && cell.value === undefined) continue;
        const formula =
          typeof cell.formula === "string"
            ? cell.formula
            : typeof cell.value === "string"
              ? cell.value
              : undefined;
        setOrClearFormulaRange(
          target,
          workbook,
          sheetName,
          cell.address,
          formula,
          (value) => this.resolveFormula(value),
        );
      }

      for (const range of worksheet.ranges ?? []) {
        if (!range.address) continue;
        if (range.action === "clear") {
          const clearType = range.clear?.clearType ?? "All";
          if (clearType === "All" || clearType === "Contents") {
            clearTrackedRange(target, workbook, sheetName, range.address);
          }
          continue;
        }
        if (range.action === "moveRange") {
          const sourceSheet =
            range.moveRange?.sourceSheetName?.trim() || sheetName;
          const destinationSheet =
            range.moveRange?.destinationSheetName?.trim() || sheetName;
          const destination = range.moveRange?.destination;
          if (!destination) {
            clearTrackedSheet(target, workbook, sourceSheet);
            clearTrackedSheet(target, workbook, destinationSheet);
            continue;
          }
          moveTrackedRange(
            target,
            workbook,
            sourceSheet,
            range.address,
            destinationSheet,
            destination,
            range.moveRange?.preserveSource === true,
          );
          continue;
        }
        if (
          range.action === "deleteRange" ||
          range.action === "insertRange" ||
          range.action === "sort"
        ) {
          clearTrackedSheet(target, workbook, sheetName);
          continue;
        }
        if (range.action !== "fill") continue;
        applyMatrixWrites(
          target,
          workbook,
          sheetName,
          range.address,
          range.formulas,
          range.values,
          (value) => this.resolveFormula(value),
        );
      }

      for (const table of worksheet.tables ?? []) {
        if (table.action !== "create") {
          clearTrackedSheet(target, workbook, sheetName);
          continue;
        }
        if (!table.address) continue;
        const tableValues = [
          ...(Array.isArray(table.headers) ? [table.headers] : []),
          ...(Array.isArray(table.rows) ? table.rows : []),
        ];
        if (tableValues.length === 0) continue;
        applyMatrixWrites(
          target,
          workbook,
          sheetName,
          table.address,
          undefined,
          tableValues,
          (value) => this.resolveFormula(value),
        );
      }
    }
  }

}

/** Return tracked cells contained by one A1 range. */
export function trackedCellsInRange(
  cells: readonly TrackedFactSetCell[],
  workbookName: string | undefined,
  sheetName: string,
  address: string,
): TrackedFactSetCell[] {
  const range = parseCellRange(address);
  if (!range) return [];
  const workbookKey = normalizeWorkbookName(workbookName);
  const sheetKey = sheetName.trim().toLowerCase();
  return cells.filter((cell) => {
    const parsed = parseCellRange(cell.address);
    return (
      parsed !== undefined &&
      normalizeWorkbookName(cell.workbookName) === workbookKey &&
      cell.sheetName.trim().toLowerCase() === sheetKey &&
      rangeContains(range, parsed.startRow, parsed.startColumn)
    );
  });
}

/** Compare the provider item identity before repairing a live cell format. */
export function sameFactSetFormulaIdentity(
  expectedFormula: string,
  liveFormula: string,
): boolean {
  if (
    !isDirectFactSetFormula(expectedFormula) ||
    !isDirectFactSetFormula(liveFormula)
  ) {
    return false;
  }
  return normalizeFormula(expectedFormula) === normalizeFormula(liveFormula);
}

function applyMatrixWrites(
  target: Map<string, TrackedFactSetCell>,
  workbookName: string,
  sheetName: string,
  address: string,
  formulas: unknown,
  values: unknown,
  resolve: (formula: unknown) => FactSetFormatHint | undefined,
): void {
  const anchor = parseCellRange(address);
  if (!anchor) return;
  const formulaRows = Array.isArray(formulas) ? formulas : [];
  const valueRows = Array.isArray(values) ? values : [];
  const rowCount = Math.max(formulaRows.length, valueRows.length);
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const formulaRow = Array.isArray(formulaRows[rowIndex])
      ? formulaRows[rowIndex]
      : [];
    const valueRow = Array.isArray(valueRows[rowIndex])
      ? valueRows[rowIndex]
      : [];
    const columnCount = Math.max(formulaRow.length, valueRow.length);
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
      const formula =
        typeof formulaRow[columnIndex] === "string"
          ? formulaRow[columnIndex]
          : typeof valueRow[columnIndex] === "string"
            ? valueRow[columnIndex]
            : undefined;
      const cellAddress =
        `${excelColumnName(anchor.startColumn + columnIndex)}` +
        `${anchor.startRow + rowIndex + 1}`;
      setOrClearFormulaRange(
        target,
        workbookName,
        sheetName,
        cellAddress,
        formula,
        resolve,
      );
    }
  }
}

function setOrClearFormulaRange(
  target: Map<string, TrackedFactSetCell>,
  workbookName: string,
  sheetName: string,
  address: string,
  formula: string | undefined,
  resolve: (formula: unknown) => FactSetFormatHint | undefined,
): void {
  const parsed = parseCellRange(address);
  if (!parsed) return;
  const hint = resolve(formula);
  const area =
    (parsed.endRow - parsed.startRow + 1) *
    (parsed.endColumn - parsed.startColumn + 1);
  if (area > 20_000) {
    if (!hint || typeof formula !== "string") {
      clearTrackedRange(target, workbookName, sheetName, address);
    }
    return;
  }
  for (let row = parsed.startRow; row <= parsed.endRow; row += 1) {
    for (
      let column = parsed.startColumn;
      column <= parsed.endColumn;
      column += 1
    ) {
      const cellAddress = `${excelColumnName(column)}${row + 1}`;
      const key = trackedCellKey(
        workbookName,
        sheetName,
        cellAddress,
      );
      if (!hint || typeof formula !== "string") {
        target.delete(key);
      } else {
        target.set(key, {
          workbookName,
          sheetName,
          address: cellAddress,
          formula,
          hint,
        });
      }
    }
  }
}

function clearTrackedRange(
  target: Map<string, TrackedFactSetCell>,
  workbookName: string,
  sheetName: string,
  address: string,
): void {
  for (const cell of trackedCellsInRange(
    Array.from(target.values()),
    workbookName,
    sheetName,
    address,
  )) {
    target.delete(trackedCellKey(workbookName, sheetName, cell.address));
  }
}

function moveTrackedRange(
  target: Map<string, TrackedFactSetCell>,
  workbookName: string,
  sourceSheetName: string,
  sourceAddress: string,
  destinationSheetName: string,
  destinationAddress: string,
  preserveSource: boolean,
): void {
  const source = parseCellRange(sourceAddress);
  const destination = parseCellRange(destinationAddress);
  if (!source || !destination) {
    clearTrackedSheet(target, workbookName, sourceSheetName);
    clearTrackedSheet(target, workbookName, destinationSheetName);
    return;
  }

  const sourceCells = trackedCellsInRange(
    Array.from(target.values()),
    workbookName,
    sourceSheetName,
    sourceAddress,
  );
  const destinationEndRow =
    destination.startRow + (source.endRow - source.startRow);
  const destinationEndColumn =
    destination.startColumn + (source.endColumn - source.startColumn);
  if (
    destinationEndRow >= EXCEL_MAX_ROWS ||
    destinationEndColumn >= EXCEL_MAX_COLUMNS
  ) {
    clearTrackedSheet(target, workbookName, sourceSheetName);
    clearTrackedSheet(target, workbookName, destinationSheetName);
    return;
  }
  const overwrittenDestination = `${excelColumnName(
    destination.startColumn,
  )}${destination.startRow + 1}:${excelColumnName(destinationEndColumn)}${
    destinationEndRow + 1
  }`;
  clearTrackedRange(
    target,
    workbookName,
    destinationSheetName,
    overwrittenDestination,
  );
  if (!preserveSource) {
    clearTrackedRange(
      target,
      workbookName,
      sourceSheetName,
      sourceAddress,
    );
  }

  for (const cell of sourceCells) {
    const parsed = parseCellRange(cell.address);
    if (!parsed) continue;
    const destinationCellAddress =
      `${excelColumnName(
        destination.startColumn + parsed.startColumn - source.startColumn,
      )}` +
      `${
        destination.startRow + parsed.startRow - source.startRow + 1
      }`;
    const moved = {
      ...cell,
      workbookName,
      sheetName: destinationSheetName,
      address: destinationCellAddress,
    };
    target.set(
      trackedCellKey(
        workbookName,
        destinationSheetName,
        destinationCellAddress,
      ),
      moved,
    );
  }
}

function clearTrackedSheet(
  target: Map<string, TrackedFactSetCell>,
  workbookName: string,
  sheetName: string,
): void {
  const sheetKey = sheetName.trim().toLowerCase();
  for (const [key, cell] of Array.from(target.entries())) {
    if (
      normalizeWorkbookName(cell.workbookName) === workbookName &&
      cell.sheetName.trim().toLowerCase() === sheetKey
    ) {
      target.delete(key);
    }
  }
}

function renameTrackedSheet(
  target: Map<string, TrackedFactSetCell>,
  workbookName: string,
  sourceSheetName: string,
  destinationSheetName: string,
): void {
  const sourceKey = sourceSheetName.trim().toLowerCase();
  const moved: TrackedFactSetCell[] = [];
  for (const [key, cell] of Array.from(target.entries())) {
    if (
      normalizeWorkbookName(cell.workbookName) === workbookName &&
      cell.sheetName.trim().toLowerCase() === sourceKey
    ) {
      target.delete(key);
      moved.push({ ...cell, sheetName: destinationSheetName });
    }
  }
  clearTrackedSheet(target, workbookName, destinationSheetName);
  for (const cell of moved) {
    target.set(
      trackedCellKey(workbookName, destinationSheetName, cell.address),
      cell,
    );
  }
}

function betterHint(
  left: FactSetFormatHint | undefined,
  right: FactSetFormatHint,
): FactSetFormatHint {
  if (!left) return right;
  return CONFIDENCE_RANK[right.confidence] > CONFIDENCE_RANK[left.confidence]
    ? right
    : left;
}

function mergeItemHints(
  existing: FactSetFormatHint | undefined,
  incoming: FactSetFormatHint,
  dataItem: string,
): FactSetFormatHint {
  if (!existing || existing.semantics === "unknown") return incoming;
  if (incoming.semantics === "unknown") return existing;
  if (existing.semantics === incoming.semantics) {
    return betterHint(existing, incoming);
  }
  if (
    CONFIDENCE_RANK[existing.confidence] !==
    CONFIDENCE_RANK[incoming.confidence]
  ) {
    return betterHint(existing, incoming);
  }
  return {
    semantics: "unknown",
    confidence: "low",
    source: "unknown",
    dataItem,
  };
}

function parseFormatHint(raw: unknown): FactSetFormatHint | undefined {
  const record = asRecord(raw);
  if (!record) return undefined;
  const semantics = record.semantics;
  const confidence = record.confidence;
  const source = record.source;
  if (
    semantics !== "percentage_points" &&
    semantics !== "decimal_fraction" &&
    semantics !== "number" &&
    semantics !== "unknown"
  ) {
    return undefined;
  }
  if (
    confidence !== "high" &&
    confidence !== "medium" &&
    confidence !== "low"
  ) {
    return undefined;
  }
  if (
    source !== "metadata" &&
    source !== "data_item" &&
    source !== "unknown"
  ) {
    return undefined;
  }
  return {
    semantics,
    confidence,
    source,
    ...(typeof record.dataItem === "string"
      ? { dataItem: record.dataItem }
      : {}),
    ...(typeof record.recommendedNumberFormat === "string"
      ? { recommendedNumberFormat: record.recommendedNumberFormat }
      : {}),
  };
}

function normalizeFormula(formula: string): string {
  return formula
    .trim()
    .replace(/^(\s*=\s*)_XLL\./i, "$1")
    .toUpperCase();
}

function normalizeWorkbookName(workbookName: string | undefined): string {
  return (workbookName ?? "").trim().toLowerCase();
}

function filterTrackedCellsByWorkbook(
  cells: Iterable<TrackedFactSetCell>,
  workbookName: string | undefined,
): TrackedFactSetCell[] {
  const values = Array.from(cells);
  if (workbookName === undefined) return values;
  const workbookKey = normalizeWorkbookName(workbookName);
  return values.filter(
    (cell) => normalizeWorkbookName(cell.workbookName) === workbookKey,
  );
}

function containsStructuralProvenanceOperation(
  modifications: WorkbookModification,
): boolean {
  if ((modifications.sheets?.length ?? 0) > 0) return true;
  return (modifications.worksheets ?? []).some((worksheet) =>
    (worksheet.ranges ?? []).some(
      (range) =>
        range.action === "moveRange" ||
        range.action === "insertRange" ||
        range.action === "deleteRange" ||
        range.action === "sort",
    ),
  );
}

function sanitizeExcelWorksheetName(sheetName: string): string {
  return sheetName.replace(/[:\\/*?[\]]/g, "_").slice(0, 31);
}

function trackedCellKey(
  workbookName: string,
  sheetName: string,
  address: string,
): string {
  return (
    `${normalizeWorkbookName(workbookName)}|` +
    `${sheetName.trim().toLowerCase()}|${address.toUpperCase()}`
  );
}

interface ParsedCellRange {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

const EXCEL_MAX_ROWS = 1_048_576;
const EXCEL_MAX_COLUMNS = 16_384;

function parseCellRange(address: string): ParsedCellRange | undefined {
  const bareAddress =
    address.trim().split("!").pop()?.replace(/\$/g, "") ?? "";
  const cellMatch = bareAddress.match(
    /^([A-Za-z]+)(\d+)(?::([A-Za-z]+)(\d+))?$/,
  );
  let startColumn: number;
  let startRow: number;
  let endColumn: number;
  let endRow: number;
  if (cellMatch) {
    startColumn = excelColumnIndex(cellMatch[1]);
    startRow = Number(cellMatch[2]) - 1;
    endColumn = cellMatch[3]
      ? excelColumnIndex(cellMatch[3])
      : startColumn;
    endRow = cellMatch[4] ? Number(cellMatch[4]) - 1 : startRow;
  } else {
    const columnMatch = bareAddress.match(
      /^([A-Za-z]+):([A-Za-z]+)$/,
    );
    if (columnMatch) {
      startColumn = excelColumnIndex(columnMatch[1]);
      endColumn = excelColumnIndex(columnMatch[2]);
      startRow = 0;
      endRow = EXCEL_MAX_ROWS - 1;
    } else {
      const rowMatch = bareAddress.match(/^(\d+):(\d+)$/);
      if (!rowMatch) return undefined;
      startColumn = 0;
      endColumn = EXCEL_MAX_COLUMNS - 1;
      startRow = Number(rowMatch[1]) - 1;
      endRow = Number(rowMatch[2]) - 1;
    }
  }
  if (
    startColumn < 0 ||
    endColumn < 0 ||
    endColumn >= EXCEL_MAX_COLUMNS ||
    startRow < 0 ||
    endRow < 0 ||
    endRow >= EXCEL_MAX_ROWS ||
    endColumn < startColumn ||
    endRow < startRow
  ) {
    return undefined;
  }
  return { startRow, endRow, startColumn, endColumn };
}

function rangeContains(
  range: ParsedCellRange,
  row: number,
  column: number,
): boolean {
  return (
    row >= range.startRow &&
    row <= range.endRow &&
    column >= range.startColumn &&
    column <= range.endColumn
  );
}

function excelColumnIndex(columnName: string): number {
  let value = 0;
  for (const char of columnName.toUpperCase()) {
    value = value * 26 + (char.charCodeAt(0) - 64);
  }
  return value - 1;
}

function excelColumnName(columnIndex: number): string {
  let value = columnIndex + 1;
  let name = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null &&
    typeof value === "object" &&
    !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
